import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../hooks/useNavigation';
import Breadcrumb from '../../components/Breadcrumb';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import type {
  CampaignTemplate,
  TemplateExecution,
  TemplateExecutionsParams,
  CampaignStatus,
} from '../../types/campaignTemplate';
import {
  Zap,
  RefreshCw,
  Filter,
  X,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Send,
  CheckCircle2,
  XCircle,
  Eye,
  Clock,
  Users,
  TrendingUp,
  DollarSign,
  ExternalLink,
  AlertCircle,
  Inbox,
  Loader2,
  Play,
  Pause,
  RotateCw,
  FileText,
  Ban,
} from 'lucide-react';

// ============================================
// STATUS CONFIG
// ============================================

const statusConfig: Record<CampaignStatus, { icon: React.ReactNode; label: string; bg: string; text: string }> = {
  draft: { icon: <FileText size={13} />, label: 'Rascunho', bg: 'bg-bg-3', text: 'text-text-2' },
  processing: { icon: <RotateCw size={13} className="animate-spin" />, label: 'Processando', bg: 'bg-info/10', text: 'text-accent-1' },
  queued: { icon: <Clock size={13} />, label: 'Na Fila', bg: 'bg-warning/10', text: 'text-warning' },
  running: { icon: <Play size={13} />, label: 'Em Execução', bg: 'bg-info/10', text: 'text-accent-1' },
  paused: { icon: <Pause size={13} />, label: 'Pausada', bg: 'bg-warning/10', text: 'text-warning' },
  completed: { icon: <CheckCircle2 size={13} />, label: 'Concluída', bg: 'bg-success/10', text: 'text-success' },
  failed: { icon: <XCircle size={13} />, label: 'Falhou', bg: 'bg-danger/10', text: 'text-danger' },
  cancelled: { icon: <Ban size={13} />, label: 'Cancelada', bg: 'bg-bg-3', text: 'text-text-3' },
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export function TemplateExecutionHistory() {
  const { id: wabaAccountId, templateId } = useParams<{ id: string; templateId: string }>();
  const { accessToken } = useAuth();
  const { navigateWithState } = useNavigation();

  const [template, setTemplate] = useState<CampaignTemplate | null>(null);
  const [executions, setExecutions] = useState<TemplateExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalExecutions, setTotalExecutions] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | 'all'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Metrics per execution (cost)
  const [metricsMap, setMetricsMap] = useState<Record<string, { cost: number | null; loaded: boolean }>>({});

  // Stats
  const [stats, setStats] = useState({
    totalExecutions: 0,
    totalRecipients: 0,
    avgDeliveryRate: 0,
    avgReadRate: 0,
  });

  // ============================================
  // FETCH FUNCTIONS
  // ============================================

  const fetchTemplate = useCallback(async () => {
    if (!accessToken || !templateId) return;
    try {
      const response = await apiClient.getCampaignTemplate(templateId, accessToken);
      if (response.success && response.data) {
        setTemplate(response.data);
      }
    } catch {
      // silencioso — o template é opcional para a UI
    }
  }, [accessToken, templateId]);

  const fetchStats = useCallback(async () => {
    if (!accessToken || !templateId) return;
    try {
      const response = await apiClient.getTemplateStats(templateId, accessToken);
      if (response.success && response.data) {
        setStats({
          totalExecutions: response.data.totalExecutions,
          totalRecipients: response.data.totalRecipients,
          avgDeliveryRate: response.data.aggregatedStats.avgDeliveryRate,
          avgReadRate: response.data.aggregatedStats.avgReadRate,
        });
      }
    } catch {
      // silencioso
    }
  }, [accessToken, templateId]);

  const fetchExecutions = useCallback(async (isRefresh = false) => {
    if (!accessToken || !templateId) return;

    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const params: TemplateExecutionsParams = {
        page: currentPage,
        limit: 20,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      };

      const response = await apiClient.getTemplateExecutions(templateId, params, accessToken);

      if (response.success && response.data) {
        setExecutions(response.data.executions);
        setTotalPages(response.data.pagination.pages);
        setTotalExecutions(response.data.pagination.total);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar histórico');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [accessToken, templateId, currentPage, statusFilter]);

  const fetchExecutionMetrics = useCallback(async (executionList: TemplateExecution[]) => {
    if (!accessToken) return;

    const idsToFetch = executionList
      .filter((exec) => !metricsMap[exec.id]?.loaded)
      .map((exec) => exec.id);

    if (idsToFetch.length === 0) return;

    // Mark as loading (loaded: false, cost: null)
    setMetricsMap((prev) => {
      const next = { ...prev };
      for (const id of idsToFetch) {
        if (!next[id]) next[id] = { cost: null, loaded: false };
      }
      return next;
    });

    // Fetch in parallel
    const results = await Promise.allSettled(
      idsToFetch.map((id) => apiClient.getCampaignMetrics(id, accessToken))
    );

    setMetricsMap((prev) => {
      const next = { ...prev };
      results.forEach((result, idx) => {
        const id = idsToFetch[idx];
        if (result.status === 'fulfilled' && result.value) {
          next[id] = { cost: result.value.totalCost, loaded: true };
        } else {
          next[id] = { cost: null, loaded: true }; // failed — show "-" not spinner
        }
      });
      return next;
    });
  }, [accessToken, metricsMap]);

  useEffect(() => {
    fetchTemplate();
    fetchStats();
  }, [fetchTemplate, fetchStats]);

  useEffect(() => {
    fetchExecutions();
  }, [fetchExecutions]);

  // Fetch metrics (cost) for visible executions
  useEffect(() => {
    if (executions.length > 0) {
      fetchExecutionMetrics(executions);
    }
  }, [executions]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    setMetricsMap({}); // Reset metrics to re-fetch costs
    fetchExecutions(true);
    fetchStats();
  };

  // ============================================
  // FORMATAÇÃO
  // ============================================

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatNumber = (v: number) => new Intl.NumberFormat('pt-BR').format(v);

  const formatDateTime = (date: string) => {
    const d = new Date(date);
    return {
      date: d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
      time: d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const calcDeliveryRate = (exec: TemplateExecution) => {
    if (exec.sentCount === 0) return 0;
    return (exec.deliveredCount / exec.sentCount) * 100;
  };

  const calcProgress = (exec: TemplateExecution) => {
    if (exec.totalRecipients === 0) return 0;
    return ((exec.sentCount + exec.failedCount) / exec.totalRecipients) * 100;
  };

  // Client-side date filter
  const filteredExecutions = executions.filter((exec) => {
    if (dateFrom && new Date(exec.createdAt) < new Date(dateFrom)) return false;
    if (dateTo) {
      const end = new Date(dateTo);
      end.setHours(23, 59, 59, 999);
      if (new Date(exec.createdAt) > end) return false;
    }
    return true;
  });

  const hasActiveFilters = statusFilter !== 'all' || dateFrom || dateTo;

  const clearFilters = () => {
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  // Custo total estimado (usa metrics map)
  const totalEstimatedCost = filteredExecutions.reduce((sum, exec) => {
    const m = metricsMap[exec.id];
    return sum + (m?.cost ?? 0);
  }, 0);
  const allCostsLoaded = filteredExecutions.every((exec) => metricsMap[exec.id]?.loaded);

  // ============================================
  // RENDER
  // ============================================

  if (loading && executions.length === 0) {
    return <LoadingSpinner fullPage text="Carregando histórico..." />;
  }

  return (
    <div className="min-h-screen bg-bg-1">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: 'Ambiente WABA', path: `/waba/${wabaAccountId}/environment` },
            { label: template?.name || 'Template' },
          ]}
        />

        {/* ======== HEADER ======== */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-text-1">Histórico de Execuções</h1>
            {template && (
              <p className="text-sm text-text-2 mt-1">
                Template: <span className="font-semibold text-text-1">{template.name}</span>
                {template.templateName && (
                  <span className="text-text-3 ml-2">({template.templateName})</span>
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-2 text-sm text-text-2 hover:text-text-1 hover:bg-bg-3 rounded-lg border border-border-1 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              Atualizar
            </button>
            {template && template.isActive && (
              <button
                onClick={() => navigateWithState(
                  `/waba/${wabaAccountId}/environment`,
                  { executeTemplateId: templateId }
                )}
                className="flex items-center gap-2 px-4 py-2 bg-success text-white rounded-lg hover:bg-success/90 text-sm font-medium transition-colors"
              >
                <Zap size={16} />
                Novo Disparo
              </button>
            )}
          </div>
        </div>

        {/* ======== STATS CARDS ======== */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          {/* Execuções */}
          <div className="bg-bg-2 rounded-xl border border-border-1 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-accent-1/10">
                <Send size={14} className="text-accent-1" />
              </div>
              <span className="text-xs text-text-3 uppercase tracking-wider">Execuções</span>
            </div>
            <p className="text-xl font-bold text-text-1">{formatNumber(stats.totalExecutions)}</p>
          </div>

          {/* Destinatários */}
          <div className="bg-bg-2 rounded-xl border border-border-1 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-accent-2/10">
                <Users size={14} className="text-accent-2" />
              </div>
              <span className="text-xs text-text-3 uppercase tracking-wider">Destinatários</span>
            </div>
            <p className="text-xl font-bold text-text-1">{formatNumber(stats.totalRecipients)}</p>
          </div>

          {/* Taxa Entrega */}
          <div className="bg-bg-2 rounded-xl border border-border-1 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-success/10">
                <CheckCircle2 size={14} className="text-success" />
              </div>
              <span className="text-xs text-text-3 uppercase tracking-wider">Entrega</span>
            </div>
            <p className="text-xl font-bold text-success">{stats.avgDeliveryRate.toFixed(1)}%</p>
          </div>

          {/* Taxa Leitura */}
          <div className="bg-bg-2 rounded-xl border border-border-1 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(var(--chart-read-rgb, 139, 92, 246), 0.1)' }}>
                <Eye size={14} className="text-accent-2" />
              </div>
              <span className="text-xs text-text-3 uppercase tracking-wider">Leitura</span>
            </div>
            <p className="text-xl font-bold text-accent-2">{stats.avgReadRate.toFixed(1)}%</p>
          </div>

          {/* Custo */}
          <div className="bg-bg-2 rounded-xl border border-border-1 p-4 col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-warning/10">
                <DollarSign size={14} className="text-warning" />
              </div>
              <span className="text-xs text-text-3 uppercase tracking-wider">Custo Est.</span>
            </div>
            <p className="text-xl font-bold text-text-1">
              {allCostsLoaded ? formatCurrency(totalEstimatedCost) : (
                <span className="flex items-center gap-1.5 text-text-3">
                  <Loader2 size={16} className="animate-spin" />
                  <span className="text-sm font-normal">Calculando...</span>
                </span>
              )}
            </p>
          </div>
        </div>

        {/* ======== FILTROS ======== */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                showFilters || hasActiveFilters
                  ? 'border-accent-1 text-accent-1 bg-accent-1/5'
                  : 'border-border-1 text-text-2 hover:text-text-1 hover:bg-bg-3'
              }`}
            >
              <Filter size={15} />
              Filtros
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full bg-accent-1" />
              )}
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-2 py-1.5 text-xs text-text-3 hover:text-text-1 transition-colors"
              >
                <X size={14} />
                Limpar
              </button>
            )}
          </div>
          <p className="text-sm text-text-3">
            {filteredExecutions.length} de {totalExecutions} execuções
          </p>
        </div>

        {showFilters && (
          <div className="bg-bg-2 rounded-xl border border-border-1 p-4 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-text-3 mb-1.5 uppercase tracking-wider">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value as CampaignStatus | 'all'); setCurrentPage(1); }}
                  className="w-full px-3 py-2 bg-bg-3 border border-border-1 rounded-lg text-sm text-text-1 focus:outline-none focus:border-accent-1"
                >
                  <option value="all">Todos os status</option>
                  <option value="running">Em Execução</option>
                  <option value="completed">Concluída</option>
                  <option value="failed">Falhou</option>
                  <option value="cancelled">Cancelada</option>
                  <option value="queued">Na Fila</option>
                  <option value="paused">Pausada</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-3 mb-1.5 uppercase tracking-wider">Data Inicial</label>
                <div className="relative">
                  <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3" />
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-9 pr-3 py-2 bg-bg-3 border border-border-1 rounded-lg text-sm text-text-1 focus:outline-none focus:border-accent-1"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-3 mb-1.5 uppercase tracking-wider">Data Final</label>
                <div className="relative">
                  <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3" />
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-9 pr-3 py-2 bg-bg-3 border border-border-1 rounded-lg text-sm text-text-1 focus:outline-none focus:border-accent-1"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-4 p-4 bg-danger/10 border border-danger/30 rounded-xl flex items-center gap-3">
            <AlertCircle className="text-danger flex-shrink-0" size={18} />
            <span className="text-danger text-sm">{error}</span>
            <button onClick={() => fetchExecutions()} className="ml-auto text-sm text-danger font-medium hover:underline">
              Tentar novamente
            </button>
          </div>
        )}

        {/* ======== LISTA DE EXECUÇÕES ======== */}
        {filteredExecutions.length === 0 ? (
          <div className="bg-bg-2 rounded-xl border border-border-1 p-12 text-center">
            <Inbox size={48} className="mx-auto text-text-3 mb-4" />
            <h3 className="text-lg font-semibold text-text-1 mb-2">
              Nenhuma execução encontrada
            </h3>
            <p className="text-sm text-text-3 max-w-md mx-auto">
              {hasActiveFilters
                ? 'Nenhum resultado com os filtros aplicados. Tente ajustar os critérios de busca.'
                : 'Este template ainda não foi executado. Inicie um disparo para ver o histórico aqui.'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 px-4 py-2 text-sm text-accent-1 hover:bg-accent-1/10 rounded-lg transition-colors"
              >
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredExecutions.map((exec) => {
              const dt = formatDateTime(exec.createdAt);
              const deliveryRate = calcDeliveryRate(exec);
              const progress = calcProgress(exec);
              const status = statusConfig[exec.status];
              const isRunning = exec.status === 'running' || exec.status === 'processing' || exec.status === 'queued';

              return (
                <div
                  key={exec.id}
                  className="bg-bg-2 rounded-xl border border-border-1 hover:border-accent-1/30 transition-all group"
                >
                  {/* Progress bar for running executions */}
                  {isRunning && progress > 0 && (
                    <div className="h-[3px] rounded-t-xl overflow-hidden bg-bg-3">
                      <div
                        className="h-full bg-accent-1 transition-all duration-500"
                        style={{ width: `${Math.min(progress, 100)}%` }}
                      />
                    </div>
                  )}

                  <div className="p-4 sm:p-5">
                    {/* Top row: Name + Status + Date */}
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-sm font-semibold text-text-1 truncate">{exec.name}</h3>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                            {status.icon}
                            {status.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-text-3">
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {dt.date} {dt.time}
                          </span>
                          {exec.templateName && (
                            <span className="truncate">{exec.templateName}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          navigateWithState(`/waba/${wabaAccountId}/campaigns/${exec.id}`, {
                            templateId,
                            templateName: template?.name,
                          })
                        }
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent-1 bg-accent-1/10 hover:bg-accent-1/20 rounded-lg transition-colors flex-shrink-0"
                      >
                        <ExternalLink size={13} />
                        Detalhes
                      </button>
                    </div>

                    {/* Metrics row */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      {/* Destinatários */}
                      <div className="flex items-center gap-2.5">
                        <div className="p-1.5 rounded-md bg-bg-3">
                          <Users size={13} className="text-text-3" />
                        </div>
                        <div>
                          <p className="text-xs text-text-3">Destinatários</p>
                          <p className="text-sm font-semibold text-text-1">{formatNumber(exec.totalRecipients)}</p>
                        </div>
                      </div>

                      {/* Enviadas */}
                      <div className="flex items-center gap-2.5">
                        <div className="p-1.5 rounded-md bg-bg-3">
                          <Send size={13} className="text-accent-1" />
                        </div>
                        <div>
                          <p className="text-xs text-text-3">Enviadas</p>
                          <p className="text-sm font-semibold text-text-1">{formatNumber(exec.sentCount)}</p>
                        </div>
                      </div>

                      {/* Entregues */}
                      <div className="flex items-center gap-2.5">
                        <div className="p-1.5 rounded-md bg-bg-3">
                          <CheckCircle2 size={13} className="text-success" />
                        </div>
                        <div>
                          <p className="text-xs text-text-3">Entregues</p>
                          <div className="flex items-baseline gap-1.5">
                            <p className="text-sm font-semibold text-text-1">{formatNumber(exec.deliveredCount)}</p>
                            {exec.failedCount > 0 && (
                              <span className="text-xs text-danger">({exec.failedCount} falhas)</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Taxa */}
                      <div className="flex items-center gap-2.5">
                        <div className="p-1.5 rounded-md bg-bg-3">
                          <TrendingUp size={13} className="text-success" />
                        </div>
                        <div>
                          <p className="text-xs text-text-3">Taxa Entrega</p>
                          <div className="flex items-center gap-2">
                            <p className={`text-sm font-semibold ${deliveryRate >= 90 ? 'text-success' : deliveryRate >= 70 ? 'text-warning' : 'text-danger'}`}>
                              {deliveryRate.toFixed(1)}%
                            </p>
                            {exec.readCount !== undefined && exec.readCount > 0 && (
                              <span className="text-xs text-text-3">| {exec.readCount} lidas</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Custo */}
                      <div className="flex items-center gap-2.5">
                        <div className="p-1.5 rounded-md bg-bg-3">
                          <DollarSign size={13} className="text-warning" />
                        </div>
                        <div>
                          <p className="text-xs text-text-3">Custo</p>
                          <p className="text-sm font-semibold text-text-1">
                            {(() => {
                              const m = metricsMap[exec.id];
                              if (!m || !m.loaded) {
                                return (
                                  <span className="text-text-3 font-normal flex items-center gap-1">
                                    <Loader2 size={11} className="animate-spin" />
                                  </span>
                                );
                              }
                              return m.cost != null ? formatCurrency(m.cost) : '-';
                            })()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ======== PAGINAÇÃO ======== */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-1.5">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-3 py-2 text-sm text-text-2 hover:text-text-1 hover:bg-bg-3 border border-border-1 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
              <span className="hidden sm:inline">Anterior</span>
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let page: number;
                if (totalPages <= 7) {
                  page = i + 1;
                } else if (currentPage <= 4) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  page = totalPages - 6 + i;
                } else {
                  page = currentPage - 3 + i;
                }

                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                      page === currentPage
                        ? 'bg-accent-1 text-white shadow-sm'
                        : 'text-text-2 hover:bg-bg-3 hover:text-text-1'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-3 py-2 text-sm text-text-2 hover:text-text-1 hover:bg-bg-3 border border-border-1 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="hidden sm:inline">Próxima</span>
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
